import { c as _c } from "react/compiler-runtime";
import { Box, Field, HStack, Text, VStack, Wrap } from '@chakra-ui/react';
import { PRESET_TAG_CHIP_PROPS } from '@src/components/sco/Advisors/shared/PresetTagPicker';
import { ComboboxItem } from '@src/components/sco/ComboboxTagInput';
import { SelectableButtons } from '@src/components/sco/SelectableButtons';
import { SkillsComboboxInput } from '@src/components/sco/SkillsComboboxInput';
import { useMemo } from 'react';
export const MAX_SKILLS = 12;
const titlesToItems = (titles: string[]): ComboboxItem[] => titles.map(title => ({
  key: title,
  title
}));
const itemsToTitles = (items: ComboboxItem[]): string[] => {
  const titles: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const title = (item.title || '').trim();
    if (!title) {
      continue;
    }
    const key = title.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    titles.push(title);
  }
  return titles;
};

/**
 * Skill selection: quick-add chips for the areas the advisor picked, plus a
 * multi-select skills combobox for anything outside those lists.
 */
export const SkillPicker = t0 => {
  const $ = _c(49);
  const {
    suggested,
    selected,
    onChange,
    disabled,
    disabledText
  } = t0;
  const atLimit = selected.length >= MAX_SKILLS;
  let t1;
  if ($[0] !== suggested) {
    t1 = new Set(suggested);
    $[0] = suggested;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const suggestedSet = t1;
  let t2;
  if ($[2] !== selected || $[3] !== suggestedSet) {
    let t3;
    if ($[5] !== suggestedSet) {
      t3 = skill => suggestedSet.has(skill);
      $[5] = suggestedSet;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = selected.filter(t3);
    $[2] = selected;
    $[3] = suggestedSet;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const selectedPresets = t2;
  let t3;
  if ($[7] !== selected || $[8] !== suggestedSet) {
    let t4;
    if ($[10] !== suggestedSet) {
      t4 = skill_0 => !suggestedSet.has(skill_0);
      $[10] = suggestedSet;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = selected.filter(t4);
    $[7] = selected;
    $[8] = suggestedSet;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const customSkills = t3;
  const remaining = Math.max(0, MAX_SKILLS - selectedPresets.length);
  if (disabled) {
    const t4 = disabledText || "Pick an area above and we'll suggest the skills founders search for.";
    let t5;
    if ($[12] !== t4) {
      t5 = <Box bg="colorPalette.solid/5" borderRadius="md" padding={4}><Text color="colorPalette.fg/60" fontSize="sm">{t4}</Text></Box>;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  const mergeSkills = _temp;
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text color="colorPalette.fg/60" textStyle="labelXs">Suggested for your areas</Text>;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = atLimit ? "orange.fg" : "colorPalette.fg/50";
  let t6;
  if ($[15] !== selected.length || $[16] !== t5) {
    t6 = <HStack gap={2} justify="space-between" wrap="wrap">{t4}<Text color={t5} fontSize="xs">{selected.length}/{MAX_SKILLS} selected</Text></HStack>;
    $[15] = selected.length;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== suggested) {
    t7 = suggested.map(_temp2);
    $[18] = suggested;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== customSkills || $[21] !== onChange) {
    t8 = options => onChange(mergeSkills(options.map(_temp3), customSkills));
    $[20] = customSkills;
    $[21] = onChange;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== selectedPresets || $[24] !== t7 || $[25] !== t8) {
    t9 = <Wrap gap={2}><SelectableButtons options={t7} selectableButtonProps={PRESET_TAG_CHIP_PROPS} value={selectedPresets} onChange={t8} /></Wrap>;
    $[23] = selectedPresets;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t6 || $[28] !== t9) {
    t10 = <VStack align="stretch" gap={2}>{t6}{t9}</VStack>;
    $[27] = t6;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Field.Label>Search for more</Field.Label>;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== customSkills) {
    t12 = titlesToItems(customSkills);
    $[31] = customSkills;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = remaining === 0 ? `Limit of ${MAX_SKILLS} reached` : "Search skills\u2026";
  let t14;
  if ($[33] !== remaining || $[34] !== t13) {
    t14 = {
      allowAddingItems: true,
      allowMultipleSelections: true,
      allowNonMatchingItems: true,
      maxSelections: remaining,
      placeholder: t13
    };
    $[33] = remaining;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== onChange || $[37] !== selectedPresets) {
    t15 = items => onChange(mergeSkills(selectedPresets, itemsToTitles(items)));
    $[36] = onChange;
    $[37] = selectedPresets;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t12 || $[40] !== t14 || $[41] !== t15) {
    t16 = <SkillsComboboxInput value={t12} comboboxTagInputProps={t14} onChange={t15} />;
    $[39] = t12;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Field.HelperText>Not in the list? Search the full skills library or type your own.</Field.HelperText>;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t16) {
    t18 = <Field.Root>{t11}{t16}{t17}</Field.Root>;
    $[44] = t16;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t10 || $[47] !== t18) {
    t19 = <VStack align="stretch" gap={4}>{t10}{t18}</VStack>;
    $[46] = t10;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
};
function _temp(presets, extras) {
  const titles = [];
  const seen = new Set();
  for (const title of [...presets, ...extras]) {
    const trimmed = title.trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    titles.push(trimmed);
  }
  return titles.slice(0, MAX_SKILLS);
}
function _temp2(skill_1) {
  return {
    key: skill_1,
    title: skill_1
  };
}
function _temp3(option) {
  return option.key;
}